import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { lazy, memo, Suspense, useContext } from 'react';
import { useSelector } from 'react-redux';
import AutoSizer from 'react-virtualized-auto-sizer';
import cn from 'classnames';
import useStyles from 'isomorphic-style-loader/useStyles';
import range from 'lodash/range';
import { MedicalRecordContext } from 'context';
import { useConversion } from 'hooks';
import App from 'modules/App';
import { GLUCOSE_CONCENTRATION_LEVELS_BOTTOM_VALUES } from 'modules/PatientResults/constants';
import { DAY_GLUCOSE_PROFILE_COLORS as COLORS } from '../constants';
import { useDayProfileData, useMaxValue } from './hooks';
import styles from './BloodGlucoseProfileAgpMiniChart.pcss';
const ResponsiveLine = /*#__PURE__*/ lazy(()=>import('@nivo/line').then((m)=>({
            default: m.ResponsiveLine
        })));
const BloodGlucoseProfileAgpMiniChart = ({ records, isInProgress })=>{
    useStyles(styles);
    const medicalRecord = useContext(MedicalRecordContext);
    const { dataType } = medicalRecord;
    const direction = useSelector(App.selectors.direction);
    const conversion = useConversion('bloodGlucoseConcentration');
    const maxValue = useMaxValue(records);
    const chartData = useDayProfileData(records);
    // TODO: Unify with main thresholds
    const lowThreshold = GLUCOSE_CONCENTRATION_LEVELS_BOTTOM_VALUES[conversion.unit].target;
    const highThreshold = GLUCOSE_CONCENTRATION_LEVELS_BOTTOM_VALUES[conversion.unit].high;
    let dataLayerColors;
    if (isInProgress) {
        dataLayerColors = [
            COLORS.placeholder
        ];
    } else {
        dataLayerColors = dataType === 'BGM' ? [
            COLORS.line
        ] : [
            COLORS.high,
            COLORS.target,
            COLORS.low,
            'transparent'
        ];
    }
    const getPointColor = (serie)=>{
        if (isInProgress) {
            return COLORS.placeholder;
        }
        if (serie.id === 'high') {
            return COLORS.high;
        }
        if (serie.id === 'low') {
            return COLORS.low;
        }
        if (serie.id === 'target') {
            return COLORS.target;
        }
        return 'transparent';
    };
    //--------------------------------------------------------------------------------------------------------------------
    const renderTargetZone = ({ innerWidth, yScale })=>{
        const yTop = yScale(highThreshold);
        const yBottom = yScale(lowThreshold);
        return /*#__PURE__*/ _jsxs("g", {
            children: [
                /*#__PURE__*/ _jsx("polygon", {
                    points: `0,${yTop} ${innerWidth},${yTop} ${innerWidth},${yBottom} 0,${yBottom}`,
                    fill: COLORS.targetZone
                }),
                "-",
                /*#__PURE__*/ _jsx("line", {
                    x1: "0",
                    y1: yTop,
                    x2: innerWidth,
                    y2: yTop,
                    style: {
                        stroke: COLORS.targetZoneBorder
                    }
                }),
                /*#__PURE__*/ _jsx("line", {
                    x1: "0",
                    y1: yBottom,
                    x2: innerWidth,
                    y2: yBottom,
                    style: {
                        stroke: COLORS.targetZoneBorder
                    }
                })
            ]
        });
    };
    const renderAxes = ({ innerWidth, innerHeight })=>/*#__PURE__*/ _jsxs("g", {
            children: [
                Array(4).map((_, number)=>/*#__PURE__*/ _jsx("line", {
                        x1: "0",
                        y1: innerHeight / 4 * number + 10,
                        x2: "8",
                        y2: innerHeight / 4 * number + 10,
                        style: {
                            stroke: COLORS.targetZoneBorder
                        }
                    }, `line-left-${number}`)),
                Array(4).map((_, number)=>/*#__PURE__*/ _jsx("line", {
                        x1: innerWidth - 8,
                        y1: innerHeight / 4 * number + 10,
                        x2: innerWidth,
                        y2: innerHeight / 4 * number + 10,
                        style: {
                            stroke: COLORS.targetZoneBorder
                        }
                    }, `line-left-${number}`)),
                /*#__PURE__*/ _jsx("line", {
                    x1: innerWidth / 2,
                    y1: innerHeight,
                    x2: innerWidth / 2,
                    y2: innerHeight - 8,
                    style: {
                        stroke: COLORS.targetZoneBorder
                    }
                }),
                /*#__PURE__*/ _jsx("line", {
                    x1: innerWidth / 2,
                    y1: "0",
                    x2: innerWidth / 2,
                    y2: "8",
                    style: {
                        stroke: COLORS.targetZoneBorder
                    }
                })
            ]
        });
    //--------------------------------------------------------------------------------------------------------------------
    /*
    IMPORTANT: Because Edge doesn't support `dominant-baseline` and RTL support is poor
    hardcoded styles overwrite ticks labels transform for current tickPadding and tickRotation values and
    must be correlated if change
   */ return(// TODO: Can we do this without AutoSizer?
    /*#__PURE__*/ _jsx(AutoSizer, {
        disableHeight: true,
        children: ({ width })=>/*#__PURE__*/ _jsx("div", {
                className: cn(styles.root, {
                    fadingLoader: isInProgress
                }),
                style: {
                    width
                },
                children: /*#__PURE__*/ _jsx(Suspense, {
                    fallback: null,
                    children: /*#__PURE__*/ _jsx(ResponsiveLine, {
                        data: chartData,
                        colors: dataLayerColors,
                        margin: {
                            top: 0,
                            right: 0,
                            bottom: 0,
                            left: 0
                        },
                        xScale: {
                            type: 'linear',
                            min: 0,
                            max: 24,
                            reverse: direction === 'rtl'
                        },
                        yScale: {
                            type: 'linear',
                            min: conversion.toDisplay(0),
                            max: conversion.toDisplay(Math.max(350, maxValue))
                        },
                        axisTop: null,
                        axisRight: null,
                        axisBottom: {
                            tickSize: 5,
                            tickPadding: 5,
                            tickRotation: 0,
                            tickValues: range(24)
                        },
                        axisLeft: null,
                        pointSize: 8,
                        pointColor: getPointColor,
                        lineWidth: 2,
                        enablePointLabel: false,
                        enableCrosshair: false,
                        curve: "monotoneX",
                        layers: [
                            'markers',
                            'axes',
                            renderTargetZone,
                            'areas',
                            dataType === 'CGM' ? 'lines' : 'points',
                            'slices',
                            'legends',
                            // renderCircles,
                            renderAxes
                        ]
                    })
                })
            })
    }));
};
export default /*#__PURE__*/ memo(BloodGlucoseProfileAgpMiniChart);
